// Sales + Purchase Drill-Down Dashboard — API client.
// CPOS-only. All endpoints are GET-with-query-params; the params map 1:1
// to the controller's argument names so the wire format stays explicit
// (vs cramming everything into a body).
//
// Returns the parsed `data` envelope (the controller wraps everything in
// generateResponse(status, message, code, data) — we just unwrap on the
// happy path and surface the message on error).

import { api } from "./api";

function unwrap(res) {
  const body = res?.data || {};
  if (body.status === false) {
    const err = new Error(body.message || "Request failed");
    err.code = body.code;
    err.payload = body.data;
    throw err;
  }
  return body.data;
}

// Drops empty / null / undefined values so the URL stays clean.
function buildParams(obj) {
  const out = {};
  Object.keys(obj || {}).forEach((k) => {
    const v = obj[k];
    if (v === null || v === undefined || v === "") return;
    out[k] = v;
  });
  return out;
}

// GET /api/sales-drill-down/aggregate
//   { level, constraints: {category, state, city, store_id}, from, to, search }
export async function fetchAggregate({ level, constraints = {}, from, to, search } = {}) {
  const res = await api.get("/sales-drill-down/aggregate", {
    params: buildParams({
      level,
      category: constraints.category,
      state: constraints.state,
      city: constraints.city,
      store_id: constraints.store_id,
      from,
      to,
      search,
    }),
  });
  return unwrap(res);
}

// GET /api/sales-drill-down/invoices
//   { store_id, metric, from, to, search }
export async function fetchInvoices({ store_id, metric, from, to, search } = {}) {
  const res = await api.get("/sales-drill-down/invoices", {
    params: buildParams({ store_id, metric, from, to, search }),
  });
  return unwrap(res);
}

// GET /api/sales-drill-down/invoice/:id?metric=
export async function fetchInvoiceDetail({ id, metric } = {}) {
  const res = await api.get(`/sales-drill-down/invoice/${encodeURIComponent(id)}`, {
    params: buildParams({ metric }),
  });
  return unwrap(res);
}

// GET /api/sales-drill-down/categories
export async function fetchCategories() {
  const res = await api.get("/sales-drill-down/categories");
  return unwrap(res);
}

// GET /api/sales-drill-down/resolve?term=...
//   Returns { match: null } for free-text or { match: {level, chain, label} }
//   when the term exactly matches a known store/city/state/category. The
//   chain has the parent-level keys (category, state, city, store_id) the
//   frontend needs to build the breadcrumb nav-stack.
export async function resolveSearch(term) {
  const res = await api.get("/sales-drill-down/resolve", {
    params: buildParams({ term }),
  });
  return unwrap(res);
}

// GET /api/sales-drill-down/insights
//   Powers the Totals & Insights panel. Same constraints / date range as
//   /aggregate so the KPI numbers line up with what the user sees when
//   they drill down. Returns { kpis, trend[], top_stores[] }.
export async function fetchInsights({ constraints = {}, from, to } = {}) {
  const res = await api.get("/sales-drill-down/insights", {
    params: buildParams({
      category: constraints.category,
      state: constraints.state,
      city: constraints.city,
      store_id: constraints.store_id,
      from,
      to,
    }),
  });
  return unwrap(res);
}
